import { sfx, tone } from '../../audio/sfx';
import { defineGame } from '../../ui/arcade';
import { rr, text, type ArcadeGame } from '../game';
import { fxKit } from './market';

// School-trip minigames around Taiwan. Original designs, drawn procedurally.

const TAU = Math.PI * 2;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

// ---------------------------------------------------------------------------
// 龍舟競賽 Dragon boat race (top view, racing up the river)
// ---------------------------------------------------------------------------
function dragonBoat(): ArcadeGame {
  const W = 300, H = 420, BANK = 44, DIST = 1900, BEAT = 0.6, BOWY = 214, BL = 112, GRAB = 28;
  const LANES = [82, 150, 218];
  interface Boat { d: number; v: number; base: number; ph: number; hull: string; trim: string; head: string; fin: number | null; you: boolean; pa: [number, number] }
  let boats: Boat[] = [];
  let t = 0, lastK = -99, lastSide = 0, sync = 1, finished = false, flag = false, flagMissed = false, endT = 0, place = 0, ft = 0, beatK = -99, nextSide = -1, drumHit = 0, wob = 0;
  const fx = fxKit();
  const you = () => boats[1];

  const stroke = (side: -1 | 1) => {
    const b = you();
    if (t < -0.15 || finished) return;
    const k = Math.round(t / BEAT), off = Math.abs(t - k * BEAT);
    let boost: number, label: string, col: string;
    if (k === lastK) {
      // Two strokes in one beat: the crew loses its rhythm.
      sync = Math.max(0, sync - 0.22);
      boost = 2;
      label = '〰';
      col = '#ff8a7a';
    } else if (off < 0.09) {
      boost = 32;
      sync = Math.min(1, sync + 0.08);
      label = 'PERFECT!';
      col = '#ffd84a';
    } else if (off < 0.18) {
      boost = 24;
      sync = Math.min(1, sync + 0.03);
      label = 'GOOD';
      col = '#ffffff';
    } else {
      boost = 5;
      sync = Math.max(0, sync - 0.12);
      label = '✗';
      col = '#ff8a7a';
    }
    if (side === lastSide) {
      boost *= 0.4;
      label = side < 0 ? '▶ !' : '◀ !';
      col = '#ffb070';
    }
    lastK = k;
    lastSide = side;
    nextSide = -side;
    b.v += boost * (0.5 + 0.5 * sync);
    b.pa[side < 0 ? 0 : 1] = 1;
    const x = LANES[1] + wob;
    fx.pop(x, BOWY - 26, label, col);
    for (let i = 0; i < 4; i++) fx.burst(x + side * 18, BOWY + 30 + i * 22, ['#ffffff', '#bfe8ff'], 3, 50, 60);
    if (boost >= 30) sfx('pop');
    else tone(240, 0.06, 'triangle', 0.04, 0, 120);
  };
  const grab = () => {
    const b = you();
    if (flag || flagMissed) return;
    if (Math.abs(b.d - DIST) < GRAB) {
      flag = true;
      sfx('fanfare');
      fx.burst(LANES[1], BOWY - 20, ['#e2493b', '#ffd84a', '#ffffff'], 26, 120);
      fx.pop(LANES[1], BOWY - 30, '🚩 +20', '#ffd84a');
    }
  };

  const drawBoat = (c: CanvasRenderingContext2D, b: Boat, x: number, y: number, tt: number) => {
    // Wake.
    c.strokeStyle = 'rgba(255,255,255,0.35)';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(x - 10, y + BL);
    c.lineTo(x - 22 - b.v * 0.1, y + BL + 30);
    c.moveTo(x + 10, y + BL);
    c.lineTo(x + 22 + b.v * 0.1, y + BL + 30);
    c.stroke();
    // Paddles: 7 rows each side, sweeping back on each stroke.
    for (let i = 0; i < 7; i++) {
      const ry = y + 24 + i * 11;
      for (const s of [-1, 1] as const) {
        const k = b.pa[s < 0 ? 0 : 1];
        const sweep = b.you ? k : 0.5 + 0.5 * Math.sin(tt * 9 + b.ph + i * 0.15);
        c.strokeStyle = '#8a5530';
        c.lineWidth = 2;
        c.beginPath();
        c.moveTo(x + s * 6, ry);
        c.lineTo(x + s * 19, ry - 7 + (1 - sweep) * 12);
        c.stroke();
      }
    }
    // Hull.
    c.fillStyle = 'rgba(0,30,60,0.25)';
    c.beginPath();
    c.ellipse(x + 3, y + BL / 2 + 4, 13, BL / 2, 0, 0, TAU);
    c.fill();
    c.fillStyle = b.hull;
    c.beginPath();
    c.moveTo(x, y + 4);
    c.quadraticCurveTo(x + 13, y + 16, x + 11, y + BL * 0.6);
    c.quadraticCurveTo(x + 9, y + BL - 4, x, y + BL);
    c.quadraticCurveTo(x - 9, y + BL - 4, x - 11, y + BL * 0.6);
    c.quadraticCurveTo(x - 13, y + 16, x, y + 4);
    c.fill();
    // Scales along the gunwale.
    c.fillStyle = b.trim;
    for (let i = 0; i < 9; i++) {
      for (const s of [-1, 1]) {
        c.beginPath();
        c.arc(x + s * 9.5, y + 20 + i * 9.5, 2.4, 0, TAU);
        c.fill();
      }
    }
    // Rowers' heads.
    for (let i = 0; i < 7; i++) {
      for (const s of [-1, 1]) {
        c.fillStyle = '#2a1810';
        c.beginPath();
        c.arc(x + s * 4, y + 24 + i * 11, 3, 0, TAU);
        c.fill();
        c.fillStyle = b.trim;
        c.fillRect(x + s * 4 - 3, y + 24 + i * 11 - 1, 6, 1.6);
      }
    }
    // Drummer on a drum near the bow.
    const hit = b.you ? drumHit : 0.5 + 0.5 * Math.sin(tt * 10.5 + b.ph);
    c.fillStyle = '#c0392b';
    c.beginPath();
    c.arc(x, y + 14, 5 + hit * 1.2, 0, TAU);
    c.fill();
    c.strokeStyle = '#ffd34e';
    c.lineWidth = 1.5;
    c.stroke();
    // Curly tail at the stern.
    const wag = Math.sin(tt * 4 + b.ph) * 0.25;
    c.save();
    c.translate(x, y + BL);
    c.rotate(wag);
    c.fillStyle = b.head;
    c.beginPath();
    c.moveTo(-6, -4);
    c.quadraticCurveTo(-14, 10, -4, 16);
    c.quadraticCurveTo(2, 10, 0, 6);
    c.quadraticCurveTo(4, 12, 10, 14);
    c.quadraticCurveTo(12, 4, 6, -4);
    c.fill();
    c.restore();
    // Dragon head at the bow: mane, horns, big eyes, whiskers.
    c.save();
    c.translate(x, y);
    const nod = Math.sin(tt * 6 + b.ph) * 0.06;
    c.rotate(nod);
    c.fillStyle = b.trim;
    for (let i = 0; i < 5; i++) {
      const a = Math.PI * (0.15 + i * 0.175);
      c.beginPath();
      c.moveTo(Math.cos(a) * 8, 4 + Math.sin(a) * 4);
      c.lineTo(Math.cos(a) * 15, 6 + Math.sin(a) * 10);
      c.lineTo(Math.cos(a + 0.3) * 8, 4 + Math.sin(a + 0.3) * 4);
      c.fill();
    }
    c.strokeStyle = b.trim;
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(-4, -4);
    c.lineTo(-8, -14);
    c.moveTo(4, -4);
    c.lineTo(8, -14);
    c.stroke();
    c.fillStyle = b.head;
    c.beginPath();
    c.ellipse(0, -4, 8, 11, 0, 0, TAU);
    c.fill();
    c.fillStyle = b.trim;
    c.beginPath();
    c.ellipse(0, -12, 5, 4, 0, 0, TAU);
    c.fill();
    for (const s of [-1, 1]) {
      c.fillStyle = '#ffffff';
      c.beginPath();
      c.arc(s * 4.5, -6, 3, 0, TAU);
      c.fill();
      c.fillStyle = '#2a1810';
      c.beginPath();
      c.arc(s * 4.8, -6.8, 1.5, 0, TAU);
      c.fill();
      c.strokeStyle = '#ffd34e';
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(s * 3, -14);
      c.quadraticCurveTo(s * 12, -18 + Math.sin(tt * 5 + s) * 3, s * 10, -24);
      c.stroke();
    }
    c.restore();
    if (b.fin !== null) text(c, `${b.fin}`, x, y + BL / 2, 16, '#ffd84a');
  };

  const g: ArcadeGame = {
    w: W, h: H, buttons: ['left', 'a', 'right'], score: 0, over: false,
    reset() {
      boats = [
        { d: 0, v: 0, base: 72, ph: 0.4, hull: '#2f8fd0', trim: '#ffffff', head: '#3fb0a0', fin: null, you: false, pa: [0, 0] },
        { d: 0, v: 0, base: 0, ph: 0, hull: '#d8342a', trim: '#ffd34e', head: '#ff8a3a', fin: null, you: true, pa: [0, 0] },
        { d: 0, v: 0, base: 80, ph: 1.9, hull: '#e8b830', trim: '#7a3a1a', head: '#57c278', fin: null, you: false, pa: [0, 0] },
      ];
      t = -4 * BEAT;
      lastK = -99;
      lastSide = 0;
      sync = 1;
      finished = false;
      flag = false;
      flagMissed = false;
      endT = 0;
      place = 0;
      ft = 0;
      beatK = -99;
      nextSide = -1;
      drumHit = 0;
      wob = 0;
      fx.clear();
      g.score = 0;
      g.over = false;
      g.won = false;
    },
    press(b, down) {
      if (!down) return;
      if (b === 'left') stroke(-1);
      else if (b === 'right') stroke(1);
      else if (b === 'a' || b === 'up') grab();
    },
    pointer(type, x, y) {
      if (type !== 'down') return;
      if (y < BOWY - 40 && Math.abs(you().d - DIST) < GRAB + 60) return grab();
      stroke(x < W / 2 ? -1 : 1);
    },
    update(dt) {
      t += dt;
      const k = Math.floor((t + 0.0001) / BEAT);
      if (k !== beatK) {
        beatK = k;
        drumHit = 1;
        if (!finished) {
          tone(k < 0 ? 330 : 110, 0.16, 'sine', 0.14, 0, k < 0 ? 330 : 55);
          if (k === 0) sfx('honk');
        }
      }
      drumHit = Math.max(0, drumHit - dt * 4);
      const me = you();
      for (const b of boats) {
        b.pa[0] = Math.max(0, b.pa[0] - dt * 3);
        b.pa[1] = Math.max(0, b.pa[1] - dt * 3);
        if (t < 0) continue;
        if (!b.you) {
          // Rivals keep a steady, slightly breathing pace.
          const target = b.fin !== null ? 20 : b.base + Math.sin(t * 0.5 + b.ph) * 6;
          b.v += (target - b.v) * dt * 1.2;
        } else {
          b.v -= b.v * (0.55 + (1 - sync) * 0.45 + (finished ? 0.8 : 0)) * dt;
        }
        b.d += b.v * dt;
        if (b.fin === null && b.d >= DIST) {
          b.fin = boats.filter((q) => q.fin !== null).length + 1;
          if (b.you) {
            finished = true;
            place = b.fin;
            ft = t;
            sfx(place === 1 ? 'fanfare' : 'ok');
            endT = 2.2;
          }
        }
      }
      if (!flag && !flagMissed && me.d > DIST + GRAB) flagMissed = true;
      sync = Math.min(1, sync + dt * 0.04);
      wob = Math.sin(t * 7) * (1 - sync) * 16;
      fx.update(dt);
      if (endT > 0) {
        endT -= dt;
        if (endT <= 0) {
          const pts = [60, 35, 15][place - 1] ?? 0;
          g.score = pts + Math.max(0, Math.round((34 - ft) * 2)) + (flag ? 20 : 0);
          g.won = place === 1;
          g.over = true;
        }
      }
    },
    draw(c, tt) {
      const cam = you().d;
      const sy = (d: number) => BOWY - (d - cam);
      // River.
      const rg = c.createLinearGradient(BANK, 0, W - BANK, 0);
      rg.addColorStop(0, '#2a7ab0');
      rg.addColorStop(0.5, '#3a9ad0');
      rg.addColorStop(1, '#2a7ab0');
      c.fillStyle = rg;
      c.fillRect(BANK, 0, W - BANK * 2, H);
      c.strokeStyle = 'rgba(255,255,255,0.18)';
      c.lineWidth = 2;
      for (let i = -1; i < 14; i++) {
        const d = Math.floor(cam / 36) * 36 + i * 36 - 120;
        const y = sy(d) + ((tt * 20) % 36);
        const x = BANK + 14 + ((d * 7.3) % (W - BANK * 2 - 40) + (W - BANK * 2)) % (W - BANK * 2 - 40);
        c.beginPath();
        c.moveTo(x, y);
        c.quadraticCurveTo(x + 8, y - 3, x + 16, y);
        c.stroke();
      }
      // Banks with crowd and zongzi stalls.
      for (const side of [0, 1]) {
        const x0 = side ? W - BANK : 0;
        c.fillStyle = '#6ab04a';
        c.fillRect(x0, 0, BANK, H);
        c.fillStyle = '#d8c090';
        c.fillRect(side ? x0 : x0 + BANK - 8, 0, 8, H);
        for (let i = -1; i < 16; i++) {
          const d = Math.floor(cam / 30) * 30 + i * 30 - 150;
          const y = sy(d);
          const id = Math.round(d / 30) + side * 7;
          if (((id % 8) + 8) % 8 === 3) {
            // Zongzi stall: little awning and green triangles.
            const sx = x0 + 4;
            rr(c, sx, y - 12, BANK - 12, 22, 3, '#8a5530');
            c.fillStyle = side ? '#e2493b' : '#ffd34e';
            c.fillRect(sx - 2, y - 16, BANK - 8, 6);
            for (let z = 0; z < 3; z++) {
              const zx = sx + 6 + z * 9, zy = y + 2;
              c.fillStyle = '#4f9a3a';
              c.beginPath();
              c.moveTo(zx, zy - 5);
              c.lineTo(zx + 4, zy + 3);
              c.lineTo(zx - 4, zy + 3);
              c.fill();
              c.strokeStyle = '#f4e2c8';
              c.lineWidth = 0.8;
              c.beginPath();
              c.moveTo(zx - 2.5, zy);
              c.lineTo(zx + 2.5, zy);
              c.stroke();
            }
          } else {
            // Cheering people, arms up and bouncing.
            const cols = ['#ff7aa0', '#3a8fd9', '#ffd34e', '#9b5fc0', '#ff9f43', '#ffffff'];
            for (let p = 0; p < 2; p++) {
              const px = x0 + 9 + p * 14 + (side ? 4 : -2);
              const hop = Math.abs(Math.sin(tt * 6 + id + p * 1.3)) * 3;
              c.fillStyle = cols[(((id * 3 + p) % cols.length) + cols.length) % cols.length];
              c.beginPath();
              c.arc(px, y + 6 - hop, 5, 0, TAU);
              c.fill();
              c.fillStyle = '#2a1810';
              c.beginPath();
              c.arc(px, y - hop, 3.2, 0, TAU);
              c.fill();
              c.strokeStyle = '#ffd2a8';
              c.lineWidth = 1.5;
              c.beginPath();
              c.moveTo(px - 3, y + 3 - hop);
              c.lineTo(px - 6, y - 5 - hop * 1.5);
              c.moveTo(px + 3, y + 3 - hop);
              c.lineTo(px + 6, y - 5 - hop * 1.5);
              c.stroke();
            }
          }
        }
      }
      // Lane buoys every 380 and the finish line.
      for (let d = 380; d < DIST; d += 380) {
        const y = sy(d);
        if (y < -10 || y > H + 10) continue;
        for (let x = BANK + 10; x < W - BANK; x += 16) {
          c.fillStyle = (x / 16) % 2 < 1 ? '#e2493b' : '#ffffff';
          c.beginPath();
          c.arc(x, y, 3, 0, TAU);
          c.fill();
        }
        text(c, `${Math.round((DIST - d) / 19)}m`, W / 2, y - 10, 10, '#ffffff');
      }
      const fy = sy(DIST);
      if (fy > -40 && fy < H + 20) {
        for (let x = BANK; x < W - BANK; x += 8) for (let r = 0; r < 2; r++) {
          c.fillStyle = (x / 8 + r) % 2 < 1 ? '#ffffff' : '#2a1810';
          c.fillRect(x, fy + r * 6, 8, 6);
        }
        // Flags to grab, one per lane.
        boats.forEach((b, i) => {
          const taken = b.you ? flag : b.fin !== null;
          if (taken) return;
          const x = LANES[i];
          c.fillStyle = '#8a5530';
          c.fillRect(x - 1.5, fy - 34, 3, 34);
          const wv = Math.sin(tt * 8 + i) * 3;
          c.fillStyle = '#e2493b';
          c.beginPath();
          c.moveTo(x + 1.5, fy - 34);
          c.quadraticCurveTo(x + 10, fy - 30 + wv, x + 18, fy - 28);
          c.lineTo(x + 1.5, fy - 20);
          c.fill();
          if (b.you && !flagMissed && Math.abs(b.d - DIST) < GRAB + 70) {
            c.strokeStyle = `rgba(255,216,74,${0.5 + 0.5 * Math.sin(tt * 14)})`;
            c.lineWidth = 2;
            c.beginPath();
            c.arc(x + 6, fy - 28, 15, 0, TAU);
            c.stroke();
          }
        });
      }
      // Boats.
      boats.forEach((b, i) => {
        const x = LANES[i] + (b.you ? wob : Math.sin(tt * 1.3 + b.ph) * 2);
        drawBoat(c, b, x, sy(b.d), tt);
      });
      fx.draw(c);

      // Progress strip.
      rr(c, BANK + 6, 8, W - BANK * 2 - 12, 8, 4, 'rgba(0,0,0,0.35)');
      boats.forEach((b, i) => {
        const px = BANK + 10 + clamp(b.d / DIST, 0, 1) * (W - BANK * 2 - 20);
        c.fillStyle = b.hull;
        c.beginPath();
        c.arc(px, 12, b.you ? 6 : 4.5, 0, TAU);
        c.fill();
        if (b.you) {
          c.strokeStyle = '#ffffff';
          c.lineWidth = 1.5;
          c.stroke();
        }
        void i;
      });
      text(c, '🏁', W - BANK - 4, 12, 11, '#fff');

      // Drum HUD.
      c.fillStyle = 'rgba(30,14,8,0.88)';
      c.fillRect(0, 346, W, H - 346);
      c.fillStyle = '#ffd34e';
      c.fillRect(0, 346, W, 3);
      const next = Math.ceil(t / BEAT) * BEAT - t;
      const ringR = 16 + (next / BEAT) * 30;
      c.strokeStyle = 'rgba(255,216,74,0.7)';
      c.lineWidth = 2.5;
      c.beginPath();
      c.arc(W / 2, 383, ringR, 0, TAU);
      c.stroke();
      const dr = 16 + drumHit * 4;
      c.fillStyle = '#c0392b';
      c.beginPath();
      c.arc(W / 2, 383, dr, 0, TAU);
      c.fill();
      c.fillStyle = '#f4e2c8';
      c.beginPath();
      c.arc(W / 2, 383, dr - 4, 0, TAU);
      c.fill();
      c.fillStyle = '#e2493b';
      c.beginPath();
      c.arc(W / 2, 383, 4, 0, TAU);
      c.fill();
      for (const s of [-1, 1]) {
        const on = nextSide === s;
        rr(c, W / 2 + s * 78 - 26, 366, 52, 34, 10, on ? '#ffd34e' : 'rgba(255,255,255,0.12)');
        text(c, s < 0 ? '◀' : '▶', W / 2 + s * 78, 383, 18, on ? '#7a3a1a' : '#fff3d0');
      }
      // Crew sync bar.
      rr(c, 12, 408, 70, 6, 3, 'rgba(255,255,255,0.15)');
      rr(c, 12, 408, Math.max(4, 70 * sync), 6, 3, sync > 0.6 ? '#7fe0a0' : sync > 0.3 ? '#ffd34e' : '#ff6a5a');
      text(c, `${Math.max(0, finished ? ft : t).toFixed(1)}s`, W - 14, 410, 11, '#fff3d0', 'right');

      // Countdown and finish banner.
      if (t < 0.5) {
        const k = Math.floor(t / BEAT);
        const s = k < 0 ? `${-k}` : 'GO!';
        if (k >= -3) {
          const f = 1 - (t / BEAT - k);
          text(c, s, W / 2, 130, 34 + f * 14, k >= 0 ? '#7fe0a0' : '#ffd84a');
        }
      }
      if (finished) {
        const medal = ['🥇', '🥈', '🥉'][place - 1] ?? '';
        rr(c, W / 2 - 80, 90, 160, 44, 14, 'rgba(20,10,6,0.7)');
        text(c, `${medal} ${place}`, W / 2, 112, 24, '#ffd84a');
      }
    },
    stars: (s) => Math.min(5, Math.floor(s / 25)),
  };
  return g;
}

// ---------------------------------------------------------------------------
// 疊石頭 Pebble stacking on Qixingtan beach (七星潭)
// ---------------------------------------------------------------------------
interface Stone { x: number; y: number; w: number; h: number; rot: number; shape: number[]; col: string; stripe: boolean; squash: number; vx: number; vy: number; vr: number }

function makeShape(): number[] {
  const out: number[] = [];
  const a1 = rnd(-0.07, 0.07), a2 = rnd(-0.05, 0.05), p1 = rnd(0, TAU), p2 = rnd(0, TAU);
  for (let i = 0; i < 20; i++) {
    const th = (i / 20) * TAU;
    out.push(1 + a1 * Math.cos(2 * th + p1) + a2 * Math.cos(3 * th + p2));
  }
  return out;
}

function drawStone(c: CanvasRenderingContext2D, s: Stone): void {
  c.save();
  c.translate(s.x, s.y);
  c.rotate(s.rot);
  c.scale(1 + s.squash * 0.08, 1 - s.squash * 0.1);
  const path = () => {
    c.beginPath();
    s.shape.forEach((k, i) => {
      const th = (i / s.shape.length) * TAU;
      // Superellipse-ish profile: flatter tops and bottoms like beach pebbles.
      const cx = Math.sign(Math.cos(th)) * Math.abs(Math.cos(th)) ** 0.8, cy = Math.sign(Math.sin(th)) * Math.abs(Math.sin(th)) ** 0.8;
      const x = cx * (s.w / 2) * k, y = cy * (s.h / 2) * k;
      if (i === 0) c.moveTo(x, y);
      else c.lineTo(x, y);
    });
    c.closePath();
  };
  c.fillStyle = 'rgba(0,0,0,0.18)';
  c.save();
  c.translate(2, 3);
  path();
  c.fill();
  c.restore();
  const g = c.createRadialGradient(-s.w * 0.2, -s.h * 0.35, 2, 0, 0, s.w * 0.6);
  g.addColorStop(0, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.3, s.col);
  g.addColorStop(1, '#6a7076');
  c.fillStyle = g;
  path();
  c.fill();
  c.save();
  path();
  c.clip();
  if (s.stripe) {
    c.strokeStyle = 'rgba(255,255,255,0.75)';
    c.lineWidth = 3;
    c.beginPath();
    c.moveTo(-s.w / 2, -s.h * 0.1);
    c.quadraticCurveTo(0, s.h * 0.25, s.w / 2, -s.h * 0.05);
    c.stroke();
  }
  c.fillStyle = 'rgba(40,40,46,0.35)';
  for (let i = 0; i < 6; i++) {
    const k = s.shape[i * 3] ?? 1;
    c.beginPath();
    c.arc(Math.cos(i * 2.3) * s.w * 0.3 * k, Math.sin(i * 1.7) * s.h * 0.25, 0.9, 0, TAU);
    c.fill();
  }
  c.restore();
  c.restore();
}

const makeShapeStatic = makeShape();

function stoneStack(): ArcadeGame {
  const W = 300, H = 420, BASE_Y = 376, LIVES = 3;
  const COLS = ['#9aa0a6', '#c8ccd0', '#e8e6e0', '#7c838b', '#b0a89c', '#d8dce0'];
  let stack: Stone[] = [];
  let flying: Stone[] = [];
  let hold: Stone | null = null;
  let drop: Stone | null = null;
  let phase = 0, cam = 0, lives = LIVES, endT = 0, wind = 0, windTarget = 0, windT = 0, height = 0, shake = 0;
  const fx = fxKit();
  const pebbles: { x: number; y: number; r: number; c: string }[] = [];
  for (let i = 0; i < 90; i++) pebbles.push({ x: Math.random() * W, y: rnd(330, H), r: rnd(1.5, 4), c: COLS[i % COLS.length] });

  const newStone = (): Stone => {
    const round = Math.random() < 0.3;
    const w = round ? rnd(36, 46) : rnd(46, 78), h = round ? w * rnd(0.7, 0.85) : rnd(17, 27);
    return { x: W / 2, y: 0, w, h, rot: 0, shape: makeShape(), col: COLS[Math.floor(Math.random() * COLS.length)], stripe: Math.random() < 0.35, squash: 0, vx: 0, vy: 0, vr: 0 };
  };
  const top = () => stack[stack.length - 1];
  const topY = () => top().y - top().h / 2;
  const lose = (stones: Stone[], dir: number) => {
    for (const s of stones) {
      s.vx = dir * rnd(50, 110);
      s.vy = rnd(-120, -40);
      s.vr = dir * rnd(2, 5);
      flying.push(s);
    }
    lives--;
    shake = 0.35;
    sfx('bad');
    if (lives <= 0) endT = 1.4;
  };
  const land = (s: Stone) => {
    const t = top();
    const dx = s.x - t.x;
    if (Math.abs(dx) > t.w / 2 * 0.92) {
      // Slid off the edge.
      lose([s], Math.sign(dx) || 1);
      return;
    }
    s.y = topY() - s.h / 2 + 2;
    s.rot = clamp((dx / t.w) * 0.3, -0.15, 0.15) + t.rot * 0.3;
    s.squash = 1;
    stack.push(s);
    // Balance: every stone must hold the centre of mass of everything above it.
    for (let i = stack.length - 2; i >= 0; i--) {
      let m = 0, mx = 0;
      for (let j = i + 1; j < stack.length; j++) {
        const a = stack[j].w * stack[j].h;
        m += a;
        mx += stack[j].x * a;
      }
      const com = mx / m + wind * 3 * (stack.length - 1 - i);
      const base = stack[i];
      if (Math.abs(com - base.x) > (base.w / 2) * (i === 0 ? 0.95 : 0.82)) {
        const fall = stack.splice(i + 1);
        lose(fall, Math.sign(com - base.x));
        height = stack.length - 1;
        return;
      }
    }
    height = stack.length - 1;
    g.score = Math.max(g.score, height);
    const perfect = Math.abs(dx) < 4;
    tone(perfect ? 1200 : 900, 0.04, 'square', 0.04);
    tone(perfect ? 800 : 600, 0.06, 'triangle', 0.04, 0.03);
    fx.burst(s.x, s.y + s.h / 2, ['#e8e6e0', '#c8ccd0'], 8, 50, 200);
    if (perfect) {
      fx.pop(s.x, s.y - 20, 'PERFECT!', '#ffd84a');
      sfx('pop');
    }
    if (height === 5) windT = 0.5;
    if (height >= 20) {
      g.won = true;
      endT = 1.2;
    }
  };
  const release = () => {
    if (!hold || drop || endT > 0) return;
    drop = hold;
    hold = null;
    drop.vy = 0;
    drop.vx = 0;
    tone(500, 0.05, 'sine', 0.03);
  };

  const g: ArcadeGame = {
    w: W, h: H, buttons: ['a'], score: 0, over: false,
    reset() {
      stack = [{ x: W / 2, y: BASE_Y, w: 150, h: 50, rot: 0, shape: makeShape(), col: '#7c838b', stripe: true, squash: 0, vx: 0, vy: 0, vr: 0 }];
      flying = [];
      hold = newStone();
      drop = null;
      phase = 0;
      cam = 0;
      lives = LIVES;
      endT = 0;
      wind = 0;
      windTarget = 0;
      windT = 0;
      height = 0;
      shake = 0;
      fx.clear();
      g.score = 0;
      g.over = false;
      g.won = false;
    },
    press(b, down) {
      if (down && (b === 'a' || b === 'down')) release();
    },
    pointer(type) {
      if (type === 'down') release();
    },
    update(dt) {
      // Wind gusts once the stack is getting tall.
      if (height >= 5) {
        windT -= dt;
        if (windT <= 0) {
          windT = rnd(3, 5.5);
          windTarget = Math.random() < 0.3 ? 0 : rnd(-1, 1) * Math.min(1, 0.4 + height * 0.04);
        }
      } else windTarget = 0;
      wind += (windTarget - wind) * dt * 1.5;
      const ty = topY();
      if (hold) {
        const sp = Math.min(3.6, 1.6 + height * 0.12);
        phase += dt * sp;
        const amp = Math.min(110, 70 + height * 4);
        hold.x = W / 2 + Math.sin(phase) * amp + wind * 14;
        hold.y = ty - 96;
        hold.rot = Math.sin(phase * 2) * 0.05;
      }
      if (drop) {
        drop.vy += 900 * dt;
        drop.vx += wind * 70 * dt;
        drop.x += drop.vx * dt;
        drop.y += drop.vy * dt;
        if (drop.y + drop.h / 2 >= ty) {
          const s = drop;
          drop = null;
          land(s);
          if (endT <= 0) hold = newStone();
        }
      }
      for (const s of stack) s.squash = Math.max(0, s.squash - dt * 5);
      for (const s of flying) {
        s.vy += 900 * dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        s.rot += s.vr * dt;
      }
      flying = flying.filter((s) => s.y - cam < H + 80 && s.y < BASE_Y + 200);
      // Camera keeps the top of the stack in view.
      const want = Math.max(0, 236 - topY());
      cam += (want - cam) * Math.min(1, dt * 3);
      shake = Math.max(0, shake - dt);
      fx.update(dt);
      if (endT > 0) {
        endT -= dt;
        if (endT <= 0) g.over = true;
      }
    },
    draw(c, t) {
      // Sky, distant mountains and the Pacific (slow parallax).
      const par = cam * 0.25;
      const sky = c.createLinearGradient(0, 0, 0, 260);
      sky.addColorStop(0, '#4aa8f0');
      sky.addColorStop(1, '#cfeeff');
      c.fillStyle = sky;
      c.fillRect(0, 0, W, H);
      c.fillStyle = '#fff6c0';
      c.beginPath();
      c.arc(236, 56 + par, 20, 0, TAU);
      c.fill();
      c.fillStyle = 'rgba(255,255,255,0.85)';
      for (let i = 0; i < 3; i++) {
        const cx = ((t * 6 + i * 120) % (W + 80)) - 40, cy = 40 + i * 34 + par;
        c.beginPath();
        c.arc(cx, cy, 12, 0, TAU);
        c.arc(cx + 14, cy - 5, 14, 0, TAU);
        c.arc(cx + 30, cy, 11, 0, TAU);
        c.fill();
      }
      c.fillStyle = '#4f8a5a';
      c.beginPath();
      c.moveTo(0, 250 + par);
      c.lineTo(0, 150 + par);
      c.lineTo(40, 128 + par);
      c.lineTo(78, 168 + par);
      c.lineTo(110, 150 + par);
      c.lineTo(150, 214 + par);
      c.lineTo(170, 250 + par);
      c.fill();
      c.fillStyle = '#6aa86a';
      c.beginPath();
      c.moveTo(0, 250 + par);
      c.lineTo(0, 190 + par);
      c.lineTo(30, 176 + par);
      c.lineTo(70, 206 + par);
      c.lineTo(110, 250 + par);
      c.fill();
      const seaY = 232 + cam * 0.6;
      const sea = c.createLinearGradient(0, seaY, 0, seaY + 110);
      sea.addColorStop(0, '#1a5aa0');
      sea.addColorStop(1, '#3aa8d8');
      c.fillStyle = sea;
      c.fillRect(0, seaY, W, H);
      c.strokeStyle = 'rgba(255,255,255,0.35)';
      c.lineWidth = 1.5;
      for (let i = 0; i < 6; i++) {
        const y = seaY + 10 + i * 16;
        c.beginPath();
        for (let x = 0; x <= W; x += 10) c.lineTo(x, y + Math.sin(x * 0.05 + t * 1.5 + i) * 2);
        c.stroke();
      }
      // World layer.
      c.save();
      c.translate(shake > 0 ? Math.sin(t * 70) * shake * 8 : 0, cam);
      const shoreY = 336 + Math.sin(t * 0.9) * 5;
      c.fillStyle = 'rgba(255,255,255,0.8)';
      c.beginPath();
      c.moveTo(0, shoreY);
      for (let x = 0; x <= W; x += 15) c.lineTo(x, shoreY + Math.sin(x * 0.08 + t * 2) * 3);
      c.lineTo(W, shoreY + 8);
      c.lineTo(0, shoreY + 8);
      c.fill();
      c.fillStyle = '#9a9c98';
      c.fillRect(0, shoreY + 6, W, 200);
      for (const p of pebbles) {
        if (p.y < shoreY + 8) continue;
        c.fillStyle = p.c;
        c.beginPath();
        c.ellipse(p.x, p.y, p.r * 1.4, p.r, 0, 0, TAU);
        c.fill();
      }
      // The stack sways a touch with the wind.
      stack.forEach((s, i) => {
        c.save();
        c.translate(wind * i * 0.4, 0);
        drawStone(c, s);
        c.restore();
      });
      for (const s of flying) drawStone(c, s);
      if (drop) drawStone(c, drop);
      if (hold) {
        c.setLineDash([3, 5]);
        c.strokeStyle = 'rgba(255,255,255,0.55)';
        c.lineWidth = 1.5;
        c.beginPath();
        c.moveTo(hold.x, hold.y + hold.h / 2 + 4);
        c.lineTo(hold.x, topY() - 2);
        c.stroke();
        c.setLineDash([]);
        drawStone(c, hold);
      }
      fx.draw(c);
      c.restore();
      // Wind streaks.
      if (Math.abs(wind) > 0.08) {
        c.strokeStyle = `rgba(255,255,255,${Math.min(0.7, Math.abs(wind))})`;
        c.lineWidth = 1.5;
        for (let i = 0; i < 6; i++) {
          const y = 70 + i * 50, len = 20 + Math.abs(wind) * 30;
          const x = ((t * 240 * Math.sign(wind) + i * 97) % (W + 60) + W + 60) % (W + 60) - 30;
          c.beginPath();
          c.moveTo(x, y);
          c.lineTo(x - Math.sign(wind) * len, y);
          c.stroke();
        }
        text(c, wind > 0 ? '💨 →' : '← 💨', W / 2, 74, 14, '#ffffff');
      }
      // HUD.
      rr(c, 8, 8, 92, 28, 14, 'rgba(20,30,50,0.45)');
      text(c, `🪨 ${height}`, 20, 22, 15, '#ffffff', 'left');
      for (let i = 0; i < LIVES; i++) {
        c.globalAlpha = i < lives ? 1 : 0.25;
        drawStone(c, { x: W - 24 - i * 26, y: 22, w: 20, h: 12, rot: 0, shape: makeShapeStatic, col: '#e8e6e0', stripe: false, squash: 0, vx: 0, vy: 0, vr: 0 });
        c.globalAlpha = 1;
      }
    },
    stars: (s) => Math.min(5, Math.floor(s / 4)),
  };
  return g;
}

defineGame('dragonBoat', dragonBoat);
defineGame('stoneStack', stoneStack);
